import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { familiesAtom, familiesLoadedAtom } from './family';
import { conversationsByFamilyAtom } from './chat';
import {
  documentsByFamilyAtom,
  linksByFamilyAtom,
  listItemsByListAtom,
  listsByFamilyAtom,
  starredByFamilyAtom,
} from './apps';
import type { User, AuthTokens } from '@twinden/shared';
import {
  familyDeletedNotesAtom,
  forgetDeletedNotes,
} from '../lib/familyDeletedNote';
import { draftsAtom, forgetDrafts } from '../lib/drafts';

// Persisted atoms. getOnInit reads localStorage on the first render; without
// it the first render sees null, ProtectedRoute bounces to /login and back,
// and the original URL (e.g. a notification's ?conversation= link) is lost.
export const accessTokenAtom = atomWithStorage<string | null>(
  'twinden:accessToken',
  null,
  undefined,
  { getOnInit: true }
);

export const refreshTokenAtom = atomWithStorage<string | null>(
  'twinden:refreshToken',
  null,
  undefined,
  { getOnInit: true }
);

/** The signed-in user; a family-link signup has no family yet (D153). */
export type CurrentUser = User & { awaitingFirstFamily?: boolean };

export const currentUserAtom = atomWithStorage<CurrentUser | null>(
  'twinden:currentUser',
  null,
  undefined,
  { getOnInit: true }
);

// Derived atoms
export const isAuthenticatedAtom = atom((get) => {
  const accessToken = get(accessTokenAtom);
  const user = get(currentUserAtom);
  return !!accessToken && !!user;
});

// Actions
export const setAuthAtom = atom(
  null,
  (_get, set, { user, tokens }: { user: CurrentUser; tokens: AuthTokens }) => {
    set(currentUserAtom, user);
    set(accessTokenAtom, tokens.accessToken);
    set(refreshTokenAtom, tokens.refreshToken);
  }
);

// Sign out through useAuth's clearAuth, not this alone: it also runs
// forgetPictures, so no opened face or picture key stays (D292).
export const clearAuthAtom = atom(null, (get, set) => {
  // A deleted family's note is this person's, not the device's (D189).
  const user = get(currentUserAtom);
  if (user) forgetDeletedNotes(user.id);
  // So are unsent drafts (M62, D228).
  if (user) forgetDrafts(user.id);
  set(draftsAtom, {});
  set(familyDeletedNotesAtom, []);
  set(currentUserAtom, null);
  set(accessTokenAtom, null);
  set(refreshTokenAtom, null);
  // The next person to sign in here doesn't see these families (D153); this
  // covers a forced sign-out as well as logout.
  set(familiesAtom, []);
  set(familiesLoadedAtom, false);
  // Nor what those families held (M66, R1-1): a shared phone is a normal
  // case, and the next person must not see the last person's chats,
  // starred messages, documents, links or lists at once.
  set(conversationsByFamilyAtom, {});
  set(starredByFamilyAtom, {});
  set(documentsByFamilyAtom, {});
  set(linksByFamilyAtom, {});
  set(listsByFamilyAtom, {});
  set(listItemsByListAtom, {});
});

/**
 * Signed in with another device's passkey (the browser's "use a phone"):
 * offer to create one on this device (M41, D93). In memory only.
 */
export const offerPasskeyHereAtom = atom(false);
